// OPS-1 (2026-05-29) — Global Time Entry modal.
// One modal, three entry points: header "+ Create → Time", claim-level
// "+ Add Time" (claim pre-filled), and Operations → Time Management (admin
// entry for another user). Target: <10s to log time. Smart activity
// suggestions come from the user's own history (GET /time/suggestions).
import { useState, useEffect, useRef, useCallback } from "react";
import { apiFetch, can, getUser } from "../api";

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function TimeEntryModal({
  claimId: fixedClaimId = null,   // when set, claim is locked (claim-level entry)
  claimLabel = null,              // display label for the locked claim
  adminMode = false,              // Operations → Time Management (enter for others)
  onClose,
  onSaved,
}) {
  const [claim, setClaim]       = useState(fixedClaimId ? { id: fixedClaimId, claim_number: claimLabel } : null);
  const [search, setSearch]     = useState("");
  const [results, setResults]   = useState([]);
  const [recent, setRecent]     = useState([]);
  const [suggestions, setSugg]  = useState([]);
  const [forUser, setForUser]   = useState(null);     // admin: target user
  const [userList, setUserList] = useState([]);
  const [form, setForm] = useState({
    work_date: todayISO(), hours: "", description: "", rate_type: "", is_billable: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  const [flash, setFlash]   = useState("");
  // H13 / LD-REV-010: the resolved rate, shown live. Before this the modal never
  // sent billing_rate_id and never displayed the rate, so every entry it created
  // silently billed $0.
  const [rateInfo, setRateInfo] = useState(null);   // {rate, source, source_label, refusal_detail}
  const hoursRef = useRef(null);

  // Load recent claims + suggestions (+ user list in admin mode) once.
  useEffect(() => {
    if (!fixedClaimId) apiFetch("/me/recent-claims").then(setRecent).catch(() => {});
    apiFetch("/time/suggestions").then(setSugg).catch(() => {});
    if (adminMode) apiFetch("/users/with-claim-assignments").then(d => setUserList(d || [])).catch(() => {});
  }, [fixedClaimId, adminMode]);

  // Claim typeahead (debounced).
  useEffect(() => {
    if (fixedClaimId || !search.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      apiFetch(`/claims?search=${encodeURIComponent(search)}&limit=8`)
        .then(d => setResults(d || [])).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [search, fixedClaimId]);

  useEffect(() => { if (claim && hoursRef.current) hoursRef.current.focus(); }, [claim]);

  // H13: resolve the rate server-side (same _resolve_time_rate chain the save
  // uses, so the preview can't disagree with the write). Debounced on Activity.
  useEffect(() => {
    if (!claim) { setRateInfo(null); return; }
    let live = true;
    const t = setTimeout(() => {
      const qs = new URLSearchParams({ claim_id: String(claim.id) });
      if (form.rate_type) qs.set("rate_type", form.rate_type);
      if (adminMode && forUser) qs.set("user_id", String(forUser));
      apiFetch(`/time/resolve-rate?${qs.toString()}`)
        .then(d => { if (live) setRateInfo(d); })
        .catch(() => { if (live) setRateInfo(null); });
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [claim, form.rate_type, adminMode, forUser]);

  const previewRate  = rateInfo ? Number(rateInfo.rate) || 0 : null;
  const previewHours = parseFloat(form.hours) || 0;
  const previewAmt   = previewRate == null ? null : Math.round(previewRate * previewHours * 100) / 100;
  const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
  // Blocked only when the entry is BILLABLE and no rate resolves — a
  // deliberately non-billable entry legitimately carries no money, so
  // un-ticking Billable is the escape hatch (and the backend agrees).
  const zeroBlocked = !!(form.is_billable && rateInfo && previewRate === 0);

  const applySuggestion = (s) => {
    setForm(f => ({ ...f, description: s.description || f.description, rate_type: s.rate_type || f.rate_type }));
    if (hoursRef.current) hoursRef.current.focus();
  };

  // Dedupe suggestions by their display label (prefer the more-specific
  // narrative; fall back to the activity/rate_type) so we don't render eight
  // identical-looking chips.
  const suggChips = (() => {
    const out = [], seen = new Set();
    for (const s of suggestions) {
      const label = ((s.description && s.description.trim()) || s.rate_type || "").trim();
      const key = label.toLowerCase();
      if (!label || seen.has(key)) continue;
      seen.add(key);
      out.push({ ...s, label });
      if (out.length >= 8) break;
    }
    return out;
  })();

  const save = useCallback(async (addAnother) => {
    setError("");
    if (!claim) { setError("Pick a claim."); return; }
    const hrs = parseFloat(form.hours);
    if (!hrs || hrs <= 0) { setError("Enter hours (> 0)."); return; }
    if (adminMode && !forUser) { setError("Pick the user this time is for."); return; }
    setSaving(true);
    try {
      const body = {
        claim_id: claim.id,
        work_date: form.work_date,
        hours: hrs,
        description: form.description || null,
        rate_type: form.rate_type || null,
        is_billable: form.is_billable,
      };
      if (adminMode && forUser) body.user_id = Number(forUser);
      // BUG-2: warn (don't block) when this looks like a duplicate of an existing
      // entry — same staff, date, hours and narrative. Skip when no narrative
      // (too noisy to match on blanks) and never let the check itself fail a save.
      const desc = (form.description || "").trim().toLowerCase();
      if (desc) {
        const targetUid = adminMode && forUser ? Number(forUser) : (getUser() || {}).id;
        try {
          const existing = await apiFetch(`/claims/${claim.id}/time`);
          const dup = (existing || []).some(e =>
            (e.source || "app") !== "vca" &&
            (targetUid == null || e.user_id === targetUid) &&
            String(e.work_date).slice(0, 10) === form.work_date &&
            parseFloat(e.hours || 0).toFixed(2) === hrs.toFixed(2) &&
            (e.description || "").trim().toLowerCase() === desc);
          if (dup && !window.confirm("A matching time entry already exists for this staff, date, hours and narrative. Log it anyway?")) {
            setSaving(false); return;
          }
        } catch { /* non-blocking */ }
      }
      await apiFetch("/time", { method: "POST", body });
      if (onSaved) onSaved();
      if (addAnother) {
        setFlash(`Logged ${hrs}h on ${claim.claim_number || "claim"}.`);
        setForm(f => ({ ...f, hours: "", description: "", rate_type: "" }));
        setTimeout(() => setFlash(""), 2500);
        if (hoursRef.current) hoursRef.current.focus();
      } else {
        onClose();
      }
    } catch (e) {
      setError(e.message || "Save failed.");
    }
    setSaving(false);
  }, [claim, form, adminMode, forUser, onSaved, onClose]);

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 540 }}>
        <div className="modal-header">
          <h3>{adminMode ? "Log Time (for staff)" : "Log Time"}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert-error">{error}</div>}
          {flash && <div style={{ background: "#dcfce7", color: "#166534", padding: "7px 10px", borderRadius: 4, fontSize: 12, marginBottom: 8 }}>✓ {flash}</div>}

          {/* Claim picker */}
          {!claim ? (
            <div className="form-group full">
              <label>Claim</label>
              <input autoFocus placeholder="Search claim # / insured / claimant…"
                     value={search} onChange={e => setSearch(e.target.value)} />
              {results.length > 0 && (
                <div style={pickList}>
                  {results.map(r => (
                    <div key={r.id} style={pickRow} onClick={() => { setClaim(r); setSearch(""); setResults([]); }}>
                      <span className="mono" style={{ color: "var(--blue)", fontWeight: 600 }}>{r.claim_number}</span>
                      <span style={{ color: "var(--text2)", marginLeft: 8, fontSize: 12 }}>{r.insured_name || r.claimant_name || ""}</span>
                    </div>
                  ))}
                </div>
              )}
              {!search && recent.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 4 }}>Recent</div>
                  <div style={pickList}>
                    {recent.slice(0, 8).map(r => (
                      <div key={r.id} style={pickRow} onClick={() => setClaim(r)}>
                        <span className="mono" style={{ color: "var(--blue)", fontWeight: 600 }}>{r.claim_number}</span>
                        <span style={{ color: "var(--text2)", marginLeft: 8, fontSize: 12 }}>{r.insured_name || r.claimant_name || ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#eef1f6", padding: "8px 12px", borderRadius: 4, marginBottom: 10 }}>
              <div>
                <span className="mono" style={{ color: "var(--blue)", fontWeight: 700 }}>{claim.claim_number}</span>
                {(claim.insured_name || claim.claimant_name) && <span style={{ color: "var(--text2)", marginLeft: 8, fontSize: 12 }}>{claim.insured_name || claim.claimant_name}</span>}
              </div>
              {!fixedClaimId && <button className="btn btn-outline" style={{ padding: "2px 8px", fontSize: 11 }} onClick={() => setClaim(null)}>Change</button>}
            </div>
          )}

          {claim && (
            <>
              {adminMode && (
                <div className="form-group full">
                  <label>Time is for</label>
                  <select value={forUser || ""} onChange={e => setForUser(e.target.value)}>
                    <option value="">Select staff member…</option>
                    {userList.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                  </select>
                </div>
              )}
              <div className="form-grid">
                <div className="form-group"><label>Date</label>
                  <input type="date" value={form.work_date} onChange={e => setForm({ ...form, work_date: e.target.value })} /></div>
                <div className="form-group"><label>Hours</label>
                  <input ref={hoursRef} type="number" step="0.1" min="0" placeholder="0.0"
                         value={form.hours} onChange={e => setForm({ ...form, hours: e.target.value })}
                         onKeyDown={e => { if (e.key === "Enter") save(false); }} /></div>
              </div>
              <div className="form-group full"><label>Activity</label>
                <input placeholder="e.g. Review medical records" value={form.rate_type} spellCheck={true}
                       onChange={e => setForm({ ...form, rate_type: e.target.value })} /></div>
              {suggChips.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "2px 0 10px" }}>
                  {suggChips.map((s, i) => (
                    <button key={i} type="button" onClick={() => applySuggestion(s)} style={chip}
                            title={s.label}>
                      {s.label.length > 36 ? s.label.slice(0, 34) + "…" : s.label}
                    </button>
                  ))}
                </div>
              )}
              <div className="form-group full"><label>Narrative</label>
                <textarea rows={2} placeholder="What did you do?" value={form.description} spellCheck={true}
                          onChange={e => setForm({ ...form, description: e.target.value })} /></div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text2)" }}>
                <input type="checkbox" checked={form.is_billable} onChange={e => setForm({ ...form, is_billable: e.target.checked })} />
                Billable
              </label>

              {/* H13 / LD-REV-010: rate x hours = amount, live. */}
              {rateInfo && (
                zeroBlocked ? (
                  <div style={zeroBox}>
                    <div style={{ fontWeight: 600, marginBottom: 3 }}>This entry would bill $0.00</div>
                    <div>{rateInfo.refusal_detail || "No rate could be resolved for this claim, Activity and staff member."}</div>
                    <div style={{ marginTop: 4, color: "var(--text2)" }}>Un-tick <strong>Billable</strong> to log it as no-charge time.</div>
                  </div>
                ) : (
                  <>
                    <div style={rateBox}>
                      <span>Rate <strong>{money(previewRate)}</strong>/hr</span>
                      <span style={{ color: "var(--text3)" }}>×</span>
                      <span>{previewHours.toFixed(2)} hrs</span>
                      <span style={{ color: "var(--text3)" }}>=</span>
                      <span style={{ fontWeight: 700 }}>{money(previewAmt)}</span>
                      {rateInfo.source_label && <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text3)" }}>from {rateInfo.source_label}</span>}
                    </div>
                    {/* Review round 2 MAJOR 2: the rate came from a GENERAL
                        hourly card that does not name this Activity. Say so —
                        it is the case where a wrong price is least visible.
                        The wording is built server-side (_rate_source_notice)
                        so the modal and the repair script agree. */}
                    {rateInfo.source_notice && (
                      <div style={genericNoteBox}>{rateInfo.source_notice}</div>
                    )}
                  </>
                )
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          {claim && <button className="btn btn-outline" disabled={saving || zeroBlocked} title={zeroBlocked ? "A billable entry can't be saved at $0 (LD-REV-010)." : undefined} onClick={() => save(true)}>Save &amp; Add Another</button>}
          {claim && <button className="btn btn-primary" disabled={saving || zeroBlocked} title={zeroBlocked ? "A billable entry can't be saved at $0 (LD-REV-010)." : undefined} onClick={() => save(false)}>{saving ? "Saving…" : "Save"}</button>}
        </div>
      </div>
    </div>
  );
}

const pickList = { border: "1px solid var(--border)", borderRadius: 4, marginTop: 4, maxHeight: 180, overflowY: "auto" };
const pickRow  = { padding: "7px 10px", cursor: "pointer", borderBottom: "1px solid var(--border-light, #eee)" };
const rateBox  = { display: "flex", alignItems: "center", gap: 6, marginTop: 10, padding: "7px 10px", borderRadius: 4,
                   background: "#f1f5f9", border: "1px solid var(--border)", fontSize: 13, color: "var(--text)" };
const zeroBox  = { marginTop: 10, padding: "8px 10px", borderRadius: 4, background: "#fef3c7",
                   border: "1px solid #fcd34d", fontSize: 12, color: "#92400e", lineHeight: 1.5 };
// A NOTICE, not a warning: the rate is usable, it just came from a general
// hourly card rather than one naming this Activity (review round 2, MAJOR 2).
const genericNoteBox = { marginTop: 6, padding: "6px 10px", borderRadius: 4, background: "#eff6ff",
                         border: "1px solid #bfdbfe", fontSize: 11.5, color: "#1e40af", lineHeight: 1.5 };
const chip     = { padding: "3px 9px", borderRadius: 12, border: "1px solid var(--border)", background: "white", fontSize: 11, cursor: "pointer", color: "var(--text2)" };
